import PropTypes from "prop-types"
import React from "react"
import { useMarina } from "src/contexts/MarinaContext"
import { formatDate } from "src/main/Billing/Items/helpers"
import { isCardPresentInFlight } from "src/main/Billing/helpers"

import OverflowMenu, { overflowMenuProps } from "src/components/OverflowMenu"
import Table from "src/components/Table/index"

import { formatCurrencyFromMinorUnits } from "src/utils/UnitConversion"
import { getCurrentMarinaSlug } from "src/utils/url/parsing/marina"

import PaymentMethodDescription from "./PaymentMethodDescription"
import PaymentStatusBadge from "./PaymentStatusBadge"

const PaymentRow = ({
  payment,
  editPayment,
  settlePayment,
  mergeInvoice,
  renderMenuItems,
  editable = true,
  mergeable = false,
  menuPosition = "bottomLeft",
}) => {
  const { presentmentCurrency } = useMarina()
  const {
    id,
    balance,
    total_amount: totalAmount,
    due_date: dueDate,
    settled_at: settledAt,
  } = payment
  const settledDate = settledAt ? formatDate(settledAt) : "-"
  const cardPresentInFlight = isCardPresentInFlight(payment)
  // A card-present payment zeroes the balance while still collecting at the reader,
  // so balance == 0 alone doesn't mean settled — treat an in-flight one as an invoice.
  const previewText =
    Number(balance) === 0 && !cardPresentInFlight
      ? "View receipt"
      : "View invoice"
  const marinaSlug = getCurrentMarinaSlug()
  const previewPath = `/manage/${marinaSlug}/payments/${id}/view`
  const handlePreviewClick = () => {
    window.open(previewPath, "_blank")
  }
  const overflowMenuProps = {
    variant: "tableRow",
    alignEnd: true,
    disabled: !editable,
    menuPosition,
  }
  if (Number(balance) !== 0) {
    overflowMenuProps.cta = {
      label: "Settle",
      onClick: () => settlePayment(payment),
      fullWidth: false,
      disabled: !editable,
    }
  }

  return (
    <Table.Row key={id} variant="parent" onClick={editPayment(payment)}>
      <Table.Cell>
        <PaymentStatusBadge payment={payment} />
      </Table.Cell>
      <Table.Cell>
        <PaymentMethodDescription
          paymentMethod={payment.payment_method}
          fallback={cardPresentInFlight ? "-" : "Manual"}
        />
      </Table.Cell>
      <Table.Cell title={formatDate(dueDate)} />
      <Table.Cell title={settledDate} />
      <Table.Cell
        title={formatCurrencyFromMinorUnits({
          amount: totalAmount,
          currency: presentmentCurrency,
        })}
      />
      <Table.Cell>
        <OverflowMenu {...overflowMenuProps}>
          <OverflowMenu.Item label="Manage" onClick={editPayment(payment)} />
          <OverflowMenu.Item
            label={previewText}
            onClick={() => handlePreviewClick()}
          />
          {mergeable && (
            <OverflowMenu.Item
              label="Merge invoices"
              onClick={() => mergeInvoice(payment)}
            />
          )}
          {renderMenuItems ? renderMenuItems(payment) : null}
        </OverflowMenu>
      </Table.Cell>
    </Table.Row>
  )
}

PaymentRow.propTypes = {
  payment: PropTypes.shape({
    id: PropTypes.string.isRequired,
    balance: PropTypes.number.isRequired,
    payment_method: PropTypes.object,
    total_amount: PropTypes.number.isRequired,
    due_date: PropTypes.string.isRequired,
    settled_at: PropTypes.string,
    display_status: PropTypes.string.isRequired,
  }),
  editPayment: PropTypes.func.isRequired,
  settlePayment: PropTypes.func.isRequired,
  mergeInvoice: PropTypes.func.isRequired,
  renderMenuItems: PropTypes.func,
  editable: PropTypes.bool.isRequired,
  mergeable: PropTypes.bool.isRequired,
  menuPosition: overflowMenuProps.menuPosition,
}

export default PaymentRow
